import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminScanSequencesService } from '../services/admin/admin-scan-sequences.service';
import { ScanSequence } from '../common/model/dto/scan-sequences/scan-sequence';
import { AddScanningPlanDto } from '../common/model/dto/scan-sequences/add-scanning-plan';
import { CreateScanSequenceRequest } from '../common/model/dto/scan-sequences/create-scan-sequence-request.dto';

export const ADMIN_SCAN_SEQUENCE_MUTATION_KEY = [
  'admin-scan-sequence-mutation',
];

export function useAdminScanSequenceMutations() {
  const qc = useQueryClient();

  const invalidateScanSequences = () =>
    qc.invalidateQueries({ queryKey: ['admin-scan-sequences'] });

  const onScanSequenceSaved = (scanSequence: ScanSequence) => {
    qc.setQueryData(['admin-scan-sequence', scanSequence.id], scanSequence);
    invalidateScanSequences();
  };

  const createScanSequence = useMutation({
    mutationKey: ADMIN_SCAN_SEQUENCE_MUTATION_KEY,
    mutationFn: (createScanSequenceRequest: CreateScanSequenceRequest) =>
      AdminScanSequencesService.createScanSequence(createScanSequenceRequest),
    onSuccess: invalidateScanSequences,
  });

  const duplicateScanSequence = useMutation({
    mutationKey: ADMIN_SCAN_SEQUENCE_MUTATION_KEY,
    mutationFn: (id: string) =>
      AdminScanSequencesService.duplicateScanSequence(id),
    onSuccess: invalidateScanSequences,
  });

  const updateScanSequence = useMutation({
    mutationKey: ADMIN_SCAN_SEQUENCE_MUTATION_KEY,
    mutationFn: ({
      scanSequenceId,
      updatedProperties,
    }: {
      scanSequenceId: string;
      updatedProperties: Partial<ScanSequence>;
    }) =>
      AdminScanSequencesService.updateScanSequence(
        scanSequenceId,
        updatedProperties
      ),
    onSuccess: onScanSequenceSaved,
  });

  const deleteScanSequence = useMutation({
    mutationKey: ADMIN_SCAN_SEQUENCE_MUTATION_KEY,
    mutationFn: (id: string) =>
      AdminScanSequencesService.deleteScanSequence(id),
    onSuccess: (_, id) => {
      qc.removeQueries({ queryKey: ['admin-scan-sequence', id] });
      invalidateScanSequences();
    },
  });

  const addScanningPlan = useMutation({
    mutationKey: ADMIN_SCAN_SEQUENCE_MUTATION_KEY,
    mutationFn: ({
      id,
      addScanningPlanDto,
    }: {
      id: string;
      addScanningPlanDto: AddScanningPlanDto;
    }) =>
      AdminScanSequencesService.addScanningPlanToScanSequence(
        id,
        addScanningPlanDto
      ),
    onSuccess: onScanSequenceSaved,
  });

  const deleteScanningPlan = useMutation({
    mutationKey: ADMIN_SCAN_SEQUENCE_MUTATION_KEY,
    mutationFn: ({ id, length }: { id: string; length: number }) =>
      AdminScanSequencesService.deleteScanningPlanFromScanSequence(id, {
        length,
      }),
    onSuccess: onScanSequenceSaved,
  });

  return {
    createScanSequence,
    duplicateScanSequence,
    updateScanSequence,
    deleteScanSequence,
    addScanningPlan,
    deleteScanningPlan,
  };
}
